import api from "@/app/api";
import { BrewManError } from "@/app/errors";
import { BuildHash } from "@/app/services/generated/constants";
import versionManager from "@/store/modules/versionManager";
import { translate, keys } from "@/plugins/i18n";
import appStore from "@/store/modules/appStore";
import { EnumSnackbarType } from "@/components/controls/snackbar/SnackbarTypes";
import { tpSessionData } from "@tp/tpSessionData";

export default class ServiceBase {
  protected async makeCommonJsonRequest<T>(
    methodName: string,
    req: any,
    isDbWrite: boolean,
    extractor: null | ((x: any) => null | any),
    sessionToken: string,
    sessionTokenHeaderName: string,
    opts?: {
      requestTimeoutMs?: number;
    }
  ): Promise<T> {
    const requestStartTimeMsSinceEpoch = new Date().getTime();
    // Capture a stack trace that can referenced on the other side of the
    // async call.
    const preAsyncStackTrace = new Error("").stack ?? "";

    let response;

    const headers: Record<string, string> = {
      "content-type": "application/json;charset=UTF-8",
    };
    headers[sessionTokenHeaderName] = sessionToken;

    try {
      response = await fetch(window.location.origin + "/webapi/" + methodName, {
        method: "POST",
        headers,
        // AbortSignal.timeout type info is not yet released: https://github.com/microsoft/TypeScript/issues/48003
        // However is support in all major browsers.
        // @ts-ignore
        signal: opts?.requestTimeoutMs
          ? AbortSignal.timeout(opts?.requestTimeoutMs)
          : undefined,
        body: JSON.stringify(req),
      });
    } catch (e: any) {
      // `fetch` can throw exceptions when a connection cannot be made (even to the GLB).
      // Add some clarity to the "Failed to fetch" message for the user
      appStore.pushSnackbarConfig({
        text: translate(keys.error_failed_to_fetch, {
          message: e.message,
        }).toString(),
        type: EnumSnackbarType.error,
      });

      throw new BrewManError(
        translate(keys.error_failed_to_fetch, {
          message: e.message,
        }).toString(),
        e
      );
    }

    // If we do not have a json response and an error status then we might be
    // hitting a proxy server error, or a load balancer error.
    if (
      !response.ok &&
      response.headers.get("content-type")?.indexOf("application/json") === -1
    ) {
      const badResponseBody = await response.text();
      throw new BrewManError(
        `Failed API request. Method: ${methodName}. Status: ${
          response.statusText
        }. ${badResponseBody ? "Body: " + badResponseBody : ""}`
      );
    }

    // Version checking
    if (response.ok && response.headers.has("x-build-hash")) {
      versionManager.compareVersions(
        response.headers.get("x-build-hash") ?? "",
        BuildHash
      );
    }

    const body = await response.json();
    let data = extractor && response.ok ? extractor(body) : body;

    // Process the interceptors
    let err = undefined;
    for (const responseInterceptor of api.responseInterceptors) {
      [err, data] = responseInterceptor(err, response, data, {
        userFriendlyServiceName: methodName,
        preAsyncStackTrace,
        requestStartTimeMsSinceEpoch,
        isDbWrite,
      });
    }

    if (err) {
      throw err;
    }

    if (!err && !response.ok) {
      throw new BrewManError(
        `Failed API request. Method: ${methodName}. Status: ${response.statusText}`
      );
    }

    return data;
  }

  protected async makeJsonRequest<T>(
    methodName: string,
    req: any,
    isDbWrite: boolean,
    extractor: null | ((x: any) => null | any),
    opts?: {
      requestTimeoutMs?: number;
      sessionToken?: string;
    }
  ): Promise<T> {
    return this.makeCommonJsonRequest(
      methodName,
      req,
      isDbWrite,
      extractor,
      opts?.sessionToken ?? api.sessionToken ?? "",
      "SESSION-TOKEN",
      opts
    );
  }

  protected async makeTpJsonRequest<T>(
    methodName: string,
    req: any,
    isDbWrite: boolean,
    extractor: null | ((x: any) => null | any),
    opts?: {
      requestTimeoutMs?: number;
      sessionToken?: string;
    }
  ): Promise<T> {
    return this.makeCommonJsonRequest(
      methodName,
      req,
      isDbWrite,
      extractor,
      opts?.sessionToken ?? tpSessionData.sessionToken ?? "",
      "Tp-Session-Token",
      opts
    );
  }
}
